import React, { useMemo, useState } from 'react';
import { Users, Check, ArrowRight, X, Clock, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEventStore } from '../../stores/useEventStore';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/Card';
import { cn } from '../../utils/cn';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/textarea';
import { ActivityDialog } from '../ui/ActivityDialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NewEventDialog } from './NewEventDialog';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  highlight: boolean;
  attendees: number;
  description?: string;
  boardInitiated?: boolean;
  explanation?: boolean;
}

// NOTE: FormattedEvent var ubrukt og er fjernet

interface EventsSectionProps {
  className?: string;
}

export function EventsSection({ className }: EventsSectionProps) {
  const { toggleAttendance, getAttendanceStatus } = useEventStore(state => ({
    toggleAttendance: state.toggleAttendance,
    getAttendanceStatus: state.getAttendanceStatus,
  }));
  const { currentResidence, residences } = useUser();
  const residenceId = currentResidence?.residence_id;
  const queryClient = useQueryClient();

  const isBoard = useMemo(() => {
    if (!residences || residences.length === 0) {
      return false;
    }
    // Sjekk om brukeren har board-rolle i noen av sine tilhørigheter
    return residences.some(r => r.role === 'board');
  }, [residences]);

  // stripped render logging

  // Hent dashboard-events via React Query (samme logikk som useEventStore)
  const { data: dashboardEvents = [], isLoading } = useQuery({
    queryKey: ['residence', residenceId, 'events'],
    enabled: Boolean(residenceId),
    queryFn: async (): Promise<any[]> => {
      if (!residenceId) return [];
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // 1) Hent brukerens lagrede upcoming events
      // 2) Hent styreopprettede events for valgt bolig, framtidige
      // 3) Hent events hvor brukeren har meldt seg på (attending)
      const [upcomingResult, boardResult, attendingResult] = await Promise.all([
        supabase
          .from('upcoming_events')
          .select(`
            event_id,
            base_post!inner (
              id,
              title,
              content,
              board_initiated,
              created_at,
              forum_event!inner (
                event_start,
                event_end,
                explanation
              )
            )
          `)
          .eq('user_id', user.id)
          .order('added_at', { ascending: false }),

        supabase
          .from('base_post')
          .select(`
            id,
            title,
            content,
            board_initiated,
            created_at,
            residence_id,
            forum_type_id,
            forum_event!inner (
              event_start,
              event_end,
              explanation
            )
          `)
          .eq('board_initiated', true)
          .eq('forum_type_id', 2)
          .eq('residence_id', residenceId)
          .gte('forum_event.event_start', new Date().toISOString()),

        supabase
          .from('event_attendance')
          .select(`
            post_id,
            base_post!inner (
              id,
              title,
              content,
              board_initiated,
              created_at,
              residence_id,
              forum_type_id,
              forum_event!inner (
                event_start,
                event_end,
                explanation
              )
            )
          `)
          .eq('user_id', user.id)
          .eq('state', 'attending')
          .gte('base_post.forum_event.event_start', new Date().toISOString())
      ]);

      const upcomingEvents = upcomingResult.data || [];
      const boardEvents = boardResult.data || [];
      const allAttendingEvents = attendingResult.data || [];

      if (upcomingResult.error) {
        console.error('Feil ved henting av upcoming events:', upcomingResult.error);
      }
      if (boardResult.error) {
        console.error('Feil ved henting av styre-events:', boardResult.error);
      }
      if (attendingResult.error) {
        console.error('Feil ved henting av attending events:', attendingResult.error);
      }

      // Filtrer attending events til kun fremtidige
      const now = new Date().toISOString();
      const attendingEvents = allAttendingEvents
        .map(ae => ae.base_post)
        .filter(e => e != null && e.forum_event?.event_start && new Date(e.forum_event.event_start) >= new Date(now));

      // Samle alle events og dedupliser
      const allEvents = [
        ...upcomingEvents.map(ue => ue.base_post),
        ...boardEvents,
        ...attendingEvents
      ];
      const uniqueEvents = allEvents.filter((event, index, self) => 
        index === self.findIndex(e => e?.id === event?.id)
      );

      // Hent attendance for alle unike post-ider og tell attending
      const allIds = uniqueEvents.map(ev => ev.id);
      let attendanceCounts: Record<number, number> = {};
      if (allIds.length > 0) {
        const { data: attendanceData, error: attendanceError } = await supabase
          .from('event_attendance')
          .select('post_id, state')
          .in('post_id', allIds)
          .eq('state', 'attending');

        if (attendanceError) {
          console.error('Feil ved henting av attendance:', attendanceError);
        }

        // Filtrer eksplisitt på 'attending' for sikkerhet (selv om query allerede gjør det)
        const attendingEntries = (attendanceData || []).filter(item => item && item.state === 'attending');
        
        attendanceCounts = attendingEntries.reduce((acc: Record<number, number>, item: { post_id: number; state: string }) => {
          if (item && item.post_id) {
          acc[item.post_id] = (acc[item.post_id] || 0) + 1;
          }
          return acc;
        }, {} as Record<number, number>);
      }

      // Berik eventene med attendees-count
      const enrichedEvents = uniqueEvents.map(ev => ({
        ...ev,
        attendees: attendanceCounts[ev.id] || 0
      }));

      return enrichedEvents;
    }
  });

  const formattedEvents = useMemo(() => 
    (dashboardEvents as any[]).map(ev => {
      const start = new Date(ev.forum_event.event_start);
      const end = ev.forum_event.event_end ? new Date(ev.forum_event.event_end) : null;
      return {
        id: String(ev.id),
        title: ev.title || '',
        date: start.toISOString().split('T')[0],
        time: end
          ? `${start.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' })}`
          : start.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' }),
        highlight: !!ev.board_initiated,
        attendees: (ev as any).attendees ?? 0,
        boardInitiated: !!ev.board_initiated,
        description: ev.content || undefined,
        explanation: ev.forum_event?.explanation || false
      } as Event;
    }) as Event[],
    [dashboardEvents]
  );

  const sortedEvents = useMemo(() => 
    [...formattedEvents].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    }),
    [formattedEvents]
  );

  const visibleEvents = useMemo(() => 
    sortedEvents.slice(0, 3),
    [sortedEvents]
  );

  const [showExplanationDialog, setShowExplanationDialog] = useState(false);
  const [explanationText, setExplanationText] = useState('');
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showNewEventDialog, setShowNewEventDialog] = useState(false);

  const handleAttendance = async (e: React.MouseEvent, eventId: string, status?: 'attending' | 'declined', event?: Event) => {
    e.stopPropagation();
    
    // Hvis brukeren prøver å melde avbud, sjekk om eventet krever begrunnelse
    if (status === 'declined' && event?.explanation) {
      // Eventet krever begrunnelse, vis dialogen først
      setPendingEventId(eventId);
      setShowExplanationDialog(true);
      return;
    }
    
    try {
      await toggleAttendance(eventId, status, status === 'declined' ? explanationText : undefined);
      // Oppdater React Query cache for å hente oppdatert antall deltakere
      await queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'events'] });
    } catch (error: any) {
      // Hvis toggleAttendance kaster feil om begrunnelse, vis dialogen
      if (error?.message === 'Begrunnelse kreves for å avslå dette eventet') {
        setPendingEventId(eventId);
        setShowExplanationDialog(true);
      } else {
        console.error('Feil ved toggle av event attendance:', error);
      }
    }
  };

  const handleExplanationSubmit = async () => {
    if (!pendingEventId || !explanationText.trim()) return;
    try {
      await toggleAttendance(pendingEventId, 'declined', explanationText);
      // Oppdater React Query cache for å hente oppdatert antall deltakere
      await queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'events'] });
      setShowExplanationDialog(false);
      setExplanationText('');
      setPendingEventId(null);
    } catch (error) {
      console.error('Feil ved sending av begrunnelse:', error);
    }
  };

  const handleNewEventSubmit = async (data: { title: string; content: string; date: string; startTime: string; endTime: string; explanation: boolean }) => {
    if (!currentResidence?.residence_id) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const eventStart = new Date(`${data.date}T${data.startTime}:00`);
      const eventEnd = new Date(`${data.date}T${data.endTime}:00`);

      // Opprett base_post først
      const { data: basePostData, error: basePostError } = await supabase
        .from('base_post')
        .insert({
          user_id: user.id,
          residence_id: currentResidence.residence_id,
          forum_type_id: 2, // Event type (2 = event, 1 = discussion)
          title: data.title.trim(),
          content: data.content.trim(),
          link: null,
          visibility: 'local',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          category_id: null,
          board_initiated: true
        })
        .select('id')
        .single();

      if (basePostError) throw basePostError;

      // Opprett forum_event med samme ID
      const { error: eventError } = await supabase
        .from('forum_event')
        .insert({
          id: basePostData.id,
          event_start: eventStart.toISOString(),
          event_end: eventEnd.toISOString(),
          explanation: data.explanation || false
        });

      if (eventError) throw eventError;

      // Oppdater cache
      await queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'events'] });
      
      setShowNewEventDialog(false);
    } catch (error) {
      console.error('Error creating event:', error);
      alert(error instanceof Error ? error.message : 'En feil oppstod ved oppretting av arrangement');
    }
  };

  return (
    <Card className={cn('w-full', className)} aria-labelledby="events-heading">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle size="lg" id="events-heading">
            Kommende hendelser
          </CardTitle>
          {isBoard && (
            <Button 
              onClick={() => setShowNewEventDialog(true)}
              className={cn(
                'flex items-center gap-1 px-2 sm:px-3 py-2 sm:py-2',
                'bg-condo-dark rounded-lg hover:bg-condo-med transition-colors text-condo-light',
                'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2'
              )}
              variant="condo"
              aria-label="Legg til arrangement"
            >
              <Plus className="w-4 h-4 font-extrabold sm:font-medium" aria-hidden="true" />
              <span className="hidden sm:flex text-sm">Legg til arrangement</span>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-condo-dark"></div>
          </div>
        ) : visibleEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>Ingen kommende hendelser</p>
          </div>
        ) : (
          <div 
            className={cn(
              'grid gap-4 grid-cols-1 -mx-2 sm:-mx-4',
              visibleEvents.length >= 2 && 'sm:grid-cols-2',
              visibleEvents.length >= 3 && 'lg:grid-cols-3'
            )}
            role="list"
            aria-label="Liste over kommende hendelser"
          >
          {visibleEvents.map((event, index) => (
            <div 
              key={`${event.title}-${index}`} 
              className={cn(
                'flex items-center gap-4 px-3 py-2 border border-gray-200 rounded-lg',
                'cursor-pointer hover:bg-gray-50 transition-colors'
              )}
              role="listitem"
              onClick={() => {
                setSelectedEvent(event);
                setShowEventDialog(true);
              }}
            >
              {/* Date box */}
              <div 
                className={cn(
                  'flex-shrink-0 rounded-lg text-center w-16 h-16',
                  'flex flex-col items-center justify-center',
                  event.boardInitiated ? 'bg-condo-light text-condo-dark' : 'bg-condo-dark text-condo-light'
                )}
                aria-label={`Dato: ${new Date(event.date).getDate().toString().padStart(2, '0')} ${new Date(event.date).toLocaleString('no', { month: 'short' }).toUpperCase()}`}
              >
                <span className="text-lg font-medium">{new Date(event.date).getDate().toString().padStart(2, '0')}</span>
                <span className="text-sm">{new Date(event.date).toLocaleString('no', { month: 'short' }).toUpperCase()}</span>
              </div>

              {/* Event details - vertical layout */}
              <div className="flex-1 min-w-0 space-y-1">
                <h3 className="text-base font-medium line-clamp-2">{event.title}</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" aria-hidden="true" />
                    <time dateTime={event.time}>{event.time}</time>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" aria-hidden="true" />
                    <span>
                      {event.attendees} {event.attendees === 1 ? 'deltaker' : 'deltakere'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-0.5">
                  {/* Meld på */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (getAttendanceStatus(event.id) !== 'attending') {
                        handleAttendance(e, event.id, 'attending', event);
                      }
                    }}
                    className={cn(
                      'flex items-center gap-1 text-sm transition-colors select-none',
                      'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded',
                      getAttendanceStatus(event.id) === 'attending'
                        ? 'text-green-600'
                        : getAttendanceStatus(event.id) === 'declined'
                        ? 'text-gray-400 hover:text-gray-500'
                        : 'text-green-600 hover:text-green-700'
                    )}
                    aria-label="Meld på"
                  >
                    <Check className="h-3 w-3" aria-hidden="true" />
                    <span>
                      {getAttendanceStatus(event.id) === 'attending' ? 'Meldt på' : 'Meld på'}
                    </span>
                  </button>

                  {/* Meld avbud */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (getAttendanceStatus(event.id) !== 'declined') {
                        handleAttendance(e, event.id, 'declined', event);
                      }
                    }}
                    className={cn(
                      'flex items-center gap-1 text-sm transition-colors select-none',
                      'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded',
                      getAttendanceStatus(event.id) === 'declined'
                        ? 'text-red-600'
                        : getAttendanceStatus(event.id) === 'attending'
                        ? 'text-gray-400 hover:text-gray-500'
                        : 'text-red-600 hover:text-red-700'
                    )}
                    aria-label="Meld avbud"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                    <span>
                      {getAttendanceStatus(event.id) === 'declined' ? 'Meldt avbud' : 'Meld avbud'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}

        <div className="flex justify-end p-2 sm:p-4">
          <Link 
            to="/events" 
            className={cn(
              "flex items-center gap-1 text-sm text-condo-dark hover:text-condo-med transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded"
            )}
          >
            <span>Se alle hendelser</span>
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      </CardContent>

      {/* Explanation Dialog */}
      <Dialog 
        open={showExplanationDialog} 
        onOpenChange={setShowExplanationDialog}
        title="Begrunnelse påkrevd"
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Begrunnelse påkrevd</h3>
          <p className="text-gray-600 mb-4">
            Dette arrangementet krever en begrunnelse når du avslår. Vennligst forklar hvorfor du ikke kan delta.
          </p>
          <Textarea
            value={explanationText}
            onChange={(e) => setExplanationText(e.target.value)}
            placeholder="Skriv din begrunnelse her..."
            rows={4}
            className="mb-4"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowExplanationDialog(false);
                setExplanationText('');
                setPendingEventId(null);
              }}
            >
              Avbryt
            </Button>
            <Button
              onClick={handleExplanationSubmit}
              disabled={!explanationText.trim()}
            >
              Send begrunnelse
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Event Dialog unified via ActivityDialog */}
      {selectedEvent && (
        <ActivityDialog
          activityId={parseInt(selectedEvent.id)}
          activityType={'event'}
          open={showEventDialog}
          onOpenChange={setShowEventDialog}
          showComments={true}
          showLikes={true}
          showAttendance={true}
          showEditButton={false}
        />
      )}

      {/* New Event Dialog */}
      {showNewEventDialog && (
        <NewEventDialog
          onClose={() => setShowNewEventDialog(false)}
          onSubmit={handleNewEventSubmit}
        />
      )}
    </Card>
  );
}